import React, { useState } from "react";
import { Col } from "react-bootstrap";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Switch,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import {
  CheckRounded,
  CloseRounded,
  ViewStream as ViewStreamIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  CheckBox as CheckBoxIcon,
} from "@material-ui/icons";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { paisesAutocomplete } from "../../../../shared/data/paises";
import {
  createFilaHome,
  editFilaHome,
  isEstaticoTrue,
} from "../../../../shared/fetchers/filasHome";

const checkIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const splitDisponibilidad = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return raw
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
};

const isActivoTrue = (val) =>
  val === true || val === 1 || val === "1" || val === "true";

const CrearFila = ({ fila, onVolver, onSaved }) => {
  const isEdit = Boolean(fila?.filaHomeName);
  const classes = useStyles();
  const queryClient = useQueryClient();

  const [nombre, setNombre] = useState(fila?.filaHomeName ?? "");
  const nombreOriginal = fila?.filaHomeName ?? "";
  const [descripcion, setDescripcion] = useState(fila?.description ?? "");
  const [disponibilidadRegional, setDisponibilidadRegional] = useState(
    splitDisponibilidad(fila?.disponibilidadRegional)
  );
  const [filaHomeNameEng, setFilaHomeNameEng] = useState(
    fila?.filaHomeNameEng ?? ""
  );
  const [descriptionEng, setDescriptionEng] = useState(
    fila?.descriptionEng ?? ""
  );
  const [activo, setActivo] = useState(isActivoTrue(fila?.activo));
  const [submitting, setSubmitting] = useState(false);
  const filaEsEstatica = isEdit && isEstaticoTrue(fila?.estatico);

  const validate = () => {
    if (!nombre.trim()) {
      toast.error("El nombre de la fila es obligatorio");
      return false;
    }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const payload = {
        nombre: nombre.trim(),
        descripcion,
        disponibilidadRegional: disponibilidadRegional.join(","),
        filaHomeNameEng: filaHomeNameEng.trim(),
        descriptionEng: descriptionEng.trim(),
        activo,
      };

      const response = isEdit
        ? await editFilaHome({
            ...payload,
            nombreOriginal,
          })
        : await createFilaHome(payload);

      if (!response?.status) {
        throw new Error(response?.info ?? "Error guardando la fila");
      }

      queryClient.invalidateQueries({ queryKey: ["filasHome"] });
      if (isEdit && nombre.trim() !== nombreOriginal) {
        queryClient.invalidateQueries({
          queryKey: ["librosFila", nombreOriginal],
        });
        queryClient.invalidateQueries({
          queryKey: ["librosFila", nombre.trim()],
        });
      }
      toast.success(isEdit ? "Fila actualizada" : "Fila creada");
      if (onSaved) onSaved();
    } catch (error) {
      toast.error(
        error?.message ||
          (isEdit ? "Error al editar la fila" : "Error al crear la fila")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Col xs={12} className="content-white-box px-4 py-4">
      <div className="d-flex align-items-center justify-content-between flex-wrap">
        <div className="d-flex align-items-center">
          <ViewStreamIcon style={{ fontSize: "22px" }} />
          <div className="admin-main-title-dark-gray ml-2 mt-1">
            {isEdit ? "Editar Fila Home" : "Crear Fila Home"}
          </div>
        </div>
        <Button
          variant="outlined"
          onClick={onVolver}
          startIcon={<CloseRounded />}
          size="small"
        >
          Volver a la lista
        </Button>
      </div>
      <div className="admin-muted-text-small mt-2 mb-3">
        Configura el nombre, la descripción, textos en inglés, la disponibilidad
        regional y el estado de la fila. Las filas inactivas no se mostrarán en
        el home.
      </div>

      {filaEsEstatica && (
        <div
          className="mb-3 p-2 rounded"
          style={{ background: "#fff8e6", border: "1px solid #ffe0a3" }}
        >
          Esta fila está marcada como <strong>fija</strong> en el sistema: su
          posición en el listado de filas activas no se puede cambiar desde el
          admin (solo se editan textos y disponibilidad).
        </div>
      )}

      <form onSubmit={submit} className={classes.form}>
        <div>
          <label className="admin-label-text d-block">Nombre *</label>
          <TextField
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            variant="outlined"
            placeholder="Nombre único de la fila"
            size="small"
            fullWidth
          />
          {isEdit && (
            <div className="admin-muted-text-small mt-1">
              Puedes cambiar el nombre; los libros asignados se conservan.
            </div>
          )}
        </div>

        <div>
          <label className="admin-label-text d-block">Descripción</label>
          <TextField
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            variant="outlined"
            placeholder="Texto que aparecerá debajo del título de la fila"
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
        </div>

        <div>
          <label className="admin-label-text d-block">Nombre (inglés)</label>
          <TextField
            value={filaHomeNameEng}
            onChange={(e) => setFilaHomeNameEng(e.target.value)}
            variant="outlined"
            placeholder="Title in English (optional)"
            size="small"
            fullWidth
          />
        </div>

        <div>
          <label className="admin-label-text d-block">
            Descripción (inglés)
          </label>
          <TextField
            value={descriptionEng}
            onChange={(e) => setDescriptionEng(e.target.value)}
            variant="outlined"
            placeholder="Subtitle in English (optional)"
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
        </div>

        <div>
          <label className="admin-label-text d-block">
            Disponibilidad regional
          </label>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={paisesAutocomplete}
            getOptionLabel={(option) => option}
            value={disponibilidadRegional}
            onChange={(_, value) => setDisponibilidadRegional(value)}
            noOptionsText="Sin resultados"
            renderOption={(option, { selected }) => (
              <>
                <Checkbox
                  icon={checkIcon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                  color="primary"
                />
                {option}
              </>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder={
                  disponibilidadRegional.length
                    ? ""
                    : "Selecciona uno o varios países"
                }
                fullWidth
              />
            )}
          />
          <div className="admin-muted-text-small mt-1">
            Si no seleccionas países, la fila estará disponible globalmente.
          </div>
        </div>

        <div>
          <FormControlLabel
            control={
              <Switch
                checked={activo}
                onChange={(e) => setActivo(e.target.checked)}
                color="primary"
              />
            }
            label={`Estado: ${activo ? "Activo" : "Inactivo"}`}
          />
          <div className="admin-muted-text-small">
            Solo las filas activas se renderizan en el home público.
          </div>
        </div>

        <div className="d-flex justify-content-end mt-2" style={{ gap: 12 }}>
          <Button
            variant="contained"
            onClick={onVolver}
            startIcon={<CloseRounded />}
            classes={{ root: classes.cancelar }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={submitting || !nombre.trim()}
            startIcon={<CheckRounded />}
            classes={{ root: classes.guardar }}
          >
            {isEdit ? "Guardar cambios" : "Crear fila"}
          </Button>
        </div>
      </form>
    </Col>
  );
};

export default CrearFila;

const useStyles = makeStyles({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
  },
  guardar: {
    background: "#81c868",
    color: "#fff",
    "&:hover": { backgroundColor: "#6fb558" },
  },
  cancelar: {
    background: "#f05050",
    color: "#fff",
    "&:hover": { backgroundColor: "#dc4545" },
  },
});
